import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';
import {
  Loader2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Download,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { purchasingAPI, qualityAPI } from '../../../lib/api';
import type { POLineSchedule, NonConformanceReport } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import {
  type PerWeekDrilldownMode,
  type DashboardFilterScope,
  MODE_TITLES,
  SCHEDULE_SORT_FIELDS,
  SCHEDULE_COLUMNS,
  NCR_SORT_FIELDS,
  NCR_COLUMNS,
  buildScheduleParams,
  buildNcrParams,
  getScheduleStatusTabs,
} from './perWeekDrilldownConfig';
import { OtifStatusPill } from './CurrentWeekDrilldownTable';
import ManualAgreementBadge from '../../purchasing/ManualAgreementBadge';
import OtifOverrideDialog from '../../purchasing/OtifOverrideDialog';
import { useAuth } from '../../../contexts/AuthContext';

export type { PerWeekDrilldownMode, DashboardFilterScope };

export interface PerWeekDrilldownProps {
  open: boolean;
  onClose: () => void;
  mode: PerWeekDrilldownMode;
  /** Window start ISO (yyyy-mm-dd). Ignored for `schedules-portfolio`. */
  windowStart: string;
  /** Window end ISO (yyyy-mm-dd). Ignored for `schedules-portfolio`. */
  windowEnd: string;
  /** Today ISO (from dashboard `data.today`). Required for portfolio + past-due modes. */
  todayIso: string;
  /** Default firm-days fallback so drilldown rows align with dashboard SQL. */
  defaultFirmDays: number;
  /** Dashboard filter scope (customer / supplier / part_number / erp_order_type). */
  filters: DashboardFilterScope;
  /** Human-readable context for the dialog title (e.g. "Week of 5 May 2026"). */
  contextLabel?: string;
}

const PAGE_SIZE = 50;

/** Build a 1-based numeric pagination spec with ellipsis. Same shape as
 *  ``ForecastDrilldownModal.buildPageList`` so the two modals look
 *  consistent — kept inline rather than extracted because it's only
 *  five lines and recharts already shares the same Button. */
function buildPageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

export function PerWeekDrilldownModal({
  open, onClose, mode, windowStart, windowEnd, todayIso,
  defaultFirmDays, filters, contextLabel,
}: PerWeekDrilldownProps) {
  const { formatDate, formatNumber } = useFormatters();

  const isNcr = mode === 'ncrs';
  const SORT_FIELDS = isNcr ? NCR_SORT_FIELDS : SCHEDULE_SORT_FIELDS;
  const { user } = useAuth();
  // Same population that operates GRN Repair; the backend enforces it too.
  const canOtifOverride = !!user?.is_superuser || !!user?.is_staff;
  const [overrideTarget, setOverrideTarget] = useState<POLineSchedule | null>(null);
  // The schedules list carries a 60s Cache-Control; without busting it the
  // just-overridden row replays with its stale verdict. A ref, not state, so
  // setting it does not reset pagination.
  const cacheBustRef = useRef<number | null>(null);

  const statusTabs = getScheduleStatusTabs(mode);
  // OTIF status slice for the OTIF past-due drilldown ('' = All denominator).
  const [otifStatus, setOtifStatus] = useState('');

  const [scheduleRows, setScheduleRows] = useState<POLineSchedule[]>([]);
  const [ncrRows, setNcrRows] = useState<NonConformanceReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [sortCol, setSortCol] = useState<string>(isNcr ? 'ncr_date' : 'schedule_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const orderingParam = useMemo(() => {
    const field = SORT_FIELDS[sortCol];
    if (!field) return isNcr ? '-ncr_date' : 'schedule_date';
    return sortDir === 'desc' ? `-${field}` : field;
  }, [sortCol, sortDir, SORT_FIELDS, isNcr]);

  const fetchPage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      if (isNcr) {
        const params = {
          ...buildNcrParams(windowStart, windowEnd, filters),
          ordering: orderingParam,
          page: targetPage,
          page_size: PAGE_SIZE,
        };
        const res = await qualityAPI.getNCRs(params);
        setNcrRows(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      } else {
        const params: Record<string, unknown> = {
          ...buildScheduleParams(mode, windowStart, windowEnd, todayIso, filters, defaultFirmDays, otifStatus),
          // Set only after an override, so the refetch skips the 60s
          // Cache-Control on the schedules list and the row returns with its
          // new verdict rather than replaying the stale one.
          ...(cacheBustRef.current ? { _t: cacheBustRef.current } : {}),
          ordering: orderingParam,
          page: targetPage,
          page_size: PAGE_SIZE,
        };
        const res = await purchasingAPI.getSchedules({ ...params, all_suppliers: true });
        setScheduleRows(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load drilldown';
      setError(msg);
      setScheduleRows([]);
      setNcrRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    isNcr, mode, windowStart, windowEnd, todayIso, defaultFirmDays,
    filters, orderingParam, otifStatus,
  ]);

  // Reset the OTIF status slice whenever the modal opens or the mode changes.
  useEffect(() => { setOtifStatus(''); }, [open, mode]);

  // Reset to page 1 every time the modal opens or mode/sort changes —
  // sorting always restarts from page 1 to avoid landing on a stale partial
  // page for the new ordering.
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageInput('1');
    fetchPage(1);
  }, [open, mode, fetchPage]);

  useEffect(() => {
    if (!open || page === 1) return;
    fetchPage(page);
  }, [page, open, fetchPage]);

  useEffect(() => { setPageInput(String(page)); }, [page]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    setPage(p);
  }, [page, totalPages, loading]);

  const handleHeaderClick = useCallback((colId: string) => {
    if (!SORT_FIELDS[colId]) return;
    if (sortCol === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
  }, [sortCol, SORT_FIELDS]);

  const sortIcon = (colId: string) => {
    if (!SORT_FIELDS[colId]) return null;
    if (sortCol !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary inline ml-1" />
      : <ArrowDown className="h-3 w-3 text-primary inline ml-1" />;
  };

  const handleExport = useCallback(async () => {
    // Server-streamed XLSX — same filters as the list call so modal count
    // == rows in workbook. Schedules and NCRs each have their own
    // export-xlsx endpoint (openpyxl-based).
    setExporting(true);
    try {
      const apiClientModule = await import('../../../lib/api/client');
      const today = new Date().toISOString().slice(0, 10);
      const xlsxMime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      let url: string;
      let downloadName: string;
      if (isNcr) {
        const params = buildNcrParams(windowStart, windowEnd, filters);
        const qs = new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString();
        url = `/quality/ncrs/export-xlsx/?${qs}`;
        downloadName = `ncrs_${today}.xlsx`;
      } else {
        const baseParams = buildScheduleParams(
          mode, windowStart, windowEnd, todayIso, filters, defaultFirmDays, otifStatus,
        );
        const qs = new URLSearchParams(
          Object.entries(baseParams)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString();
        url = `/purchasing/schedules/export-xlsx/?${qs}`;
        downloadName = `schedules_${mode}_${today}.xlsx`;
      }
      const resp = await apiClientModule.default.get(url, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: xlsxMime });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(objectUrl);
      toast.success('Excel export ready');
    } catch (err) {
      console.error('Drilldown Excel export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [
    isNcr, mode, windowStart, windowEnd, todayIso,
    defaultFirmDays, filters, otifStatus,
  ]);

  const pageButtons = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);

  const columns = isNcr ? NCR_COLUMNS : SCHEDULE_COLUMNS;
  // Override applies to schedules only, and only for staff.
  const showOverrideCol = !isNcr && canOtifOverride;
  const rowCount = isNcr ? ncrRows.length : scheduleRows.length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{MODE_TITLES[mode]}</DialogTitle>
              <DialogDescription>
                {total.toLocaleString()} {isNcr ? 'NCR' : 'schedule'}{total === 1 ? '' : 's'}
                {contextLabel ? <> · {contextLabel}</> : null}
                {filters.part_number ? <> · part <span className="font-mono">{filters.part_number}</span></> : null}
                {filters.supplier_id ? <> · supplier #{filters.supplier_id}</> : null}
              </DialogDescription>
            </div>
            <Button
              size="sm" variant="outline"
              onClick={handleExport}
              disabled={exporting || rowCount === 0}
              className="shrink-0"
            >
              {exporting
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {statusTabs && (
          <div className="flex flex-wrap gap-1 border-b pb-2">
            {statusTabs.map((t) => {
              const active = t.value === otifStatus;
              return (
                <button
                  key={t.value || 'all'}
                  onClick={() => { if (t.value !== otifStatus) setOtifStatus(t.value); }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  aria-current={active ? 'true' : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 py-3">{error}</div>
        )}

        {loading && rowCount === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rowCount === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No records match this drilldown.</div>
        ) : (
          <div className="overflow-auto max-h-[60vh] border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  {columns.map((c) => {
                    const sortable = SORT_FIELDS[c.id] !== null;
                    const align = c.align === 'right' ? 'text-right' : 'text-left';
                    const cursor = sortable ? 'cursor-pointer hover:bg-slate-100' : '';
                    return (
                      <th
                        key={c.id}
                        className={`px-3 py-2 font-medium text-slate-700 select-none ${align} ${cursor}`}
                        onClick={() => handleHeaderClick(c.id)}
                        aria-sort={
                          sortable && sortCol === c.id
                            ? (sortDir === 'asc' ? 'ascending' : 'descending')
                            : undefined
                        }
                      >
                        {c.label}{sortIcon(c.id)}
                      </th>
                    );
                  })}
                  {showOverrideCol && (
                    <th className="px-3 py-2 font-medium text-slate-700 text-left whitespace-nowrap" title="Staff: manually override the OTIF verdict">
                      Override
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isNcr
                  ? ncrRows.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.ncr_date)}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        <a
                          href={`/quality/ncr/${r.id}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                          title="Open NCR in a new tab"
                        >
                          {r.ncr_number}
                        </a>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.part_number}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.supplier_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(r.reject_qty || 0)}</td>
                      <td className="px-3 py-2"><Badge variant="outline">{r.severity_display || r.severity}</Badge></td>
                      <td className="px-3 py-2 text-xs">{r.status_display || r.status}</td>
                      <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.defect_description}>{r.defect_description || '—'}</td>
                    </tr>
                  ))
                  : scheduleRows.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{r.schedule_date ? formatDate(r.schedule_date) : '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.received_date ? formatDate(r.received_date) : '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.supplier_promise_date ? formatDate(r.supplier_promise_date) : '—'}</td>
                      {/* CR-1154: PO number links to the PO header; line to the line item. */}
                      <td className="px-3 py-2 font-mono text-xs">
                        <PoHeaderLink poLineId={r.po_line_id} poNumber={r.po_number}>{r.po_number}</PoHeaderLink>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <PoLineLink poLineId={r.po_line_id}>{r.line_number}</PoLineLink>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.schedule_line_number}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.part_number}</td>
                      <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.part_description}>{r.part_description || '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.supplier_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.schedule_qty || '0'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.shipped_qty || '0'))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.balance_qty || '0'))}</td>
                      {/* The column is OTIF Status — it was rendering r.status
                          (active / shipped / received), so the header promised a
                          pass/fail verdict and the cell showed a shipping state. */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <OtifStatusPill
                            value={r.otif_status}
                            overridden={r.otif_overridden}
                            overrideReason={r.otif_override_reason}
                          />
                          <ManualAgreementBadge schedule={r} compact />
                        </div>
                      </td>
                      {showOverrideCol && (
                        <td className="px-3 py-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={() => setOverrideTarget(r)}
                          >
                            {r.otif_overridden ? 'Manage' : 'Override'}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => goToPage(page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageButtons.map((p, idx) => (
                p === '…' ? (
                  <span key={`gap-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                ) : (
                  <Button
                    key={p}
                    size="sm"
                    variant={p === page ? 'default' : 'outline'}
                    disabled={loading}
                    onClick={() => goToPage(p)}
                    aria-current={p === page ? 'page' : undefined}
                    className="min-w-[2.25rem]"
                  >
                    {p}
                  </Button>
                )
              ))}
              <Button
                size="sm" variant="outline"
                disabled={page >= totalPages || loading}
                onClick={() => goToPage(page + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <label htmlFor="pw-page-jump">Go to page</label>
              <input
                id="pw-page-jump"
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                disabled={loading}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = parseInt(pageInput, 10);
                    if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  }
                }}
                onBlur={() => {
                  const n = parseInt(pageInput, 10);
                  if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  else setPageInput(String(page));
                }}
                className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>
          </div>
        )}
        {/* Staff-only manual OTIF override — same dialog the weekly drilldown
            uses, so a verdict is changed the same way from any tab. Refetches
            the current page so the pill updates in place. */}
        <OtifOverrideDialog
          open={overrideTarget !== null}
          schedule={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onDone={() => { cacheBustRef.current = Date.now(); fetchPage(page); }}
        />
      </DialogContent>
    </Dialog>
  );
}
